/**
 * Package grouping by canonical method — never by stale display labels.
 */

import {
  getCanonicalPackageLabel,
  getStableDisputeItemId,
  normalizeDisputeMethod,
  normalizeMethodKey,
} from './normalizeDisputeMethod';

/**
 * Merge resolver assignments onto items by stable ID (never array index).
 */
export function mergeAssignmentsByStableId(items = [], itemAssignments = {}) {
  const byId = new Map();
  Object.entries(itemAssignments || {}).forEach(([rawId, assignment]) => {
    const id = String(rawId);
    if (!id) return;
    byId.set(id, assignment);
  });

  return (items || []).map((item) => {
    const id = getStableDisputeItemId(item);
    const assignment = byId.get(id) || null;
    const normalized = normalizeDisputeMethod(item, assignment);
    return {
      ...item,
      id,
      assignment,
      disputeFamily: normalized.disputeFamily,
      method: normalized.method,
      isCollection: normalized.isCollection,
      is_collection: normalized.isCollection,
      dispute_family: normalized.disputeFamily,
      resolved_method: normalized.method,
    };
  });
}

/**
 * Group normalized items into packages by canonical method key.
 * Unresolved items are never placed into Structural.
 */
export function groupPackagesByCanonicalMethod(normalizedItems = []) {
  const packagesByMethod = new Map();

  (normalizedItems || []).forEach((item) => {
    const methodKey = normalizeMethodKey(item.method || item.resolved_method);
    if (methodKey === 'unresolved' || methodKey === 'unassigned') {
      const unresolved = packagesByMethod.get('unresolved') || {
        method_key: 'unresolved',
        method_name: getCanonicalPackageLabel('unresolved'),
        display_name: getCanonicalPackageLabel('unresolved'),
        items: [],
        count: 0,
      };
      unresolved.items.push(item);
      unresolved.count += 1;
      packagesByMethod.set('unresolved', unresolved);
      return;
    }

    const existing = packagesByMethod.get(methodKey) || {
      method_key: methodKey,
      method_name: getCanonicalPackageLabel(methodKey),
      display_name: getCanonicalPackageLabel(methodKey),
      items: [],
      count: 0,
    };
    existing.items.push(item);
    existing.count += 1;
    packagesByMethod.set(methodKey, existing);
  });

  return Array.from(packagesByMethod.values());
}

/**
 * Assert no collection items landed in a structural package.
 */
export function assertNoCollectionsInStructural(packages = []) {
  const structural = packages.find((pkg) =>
    normalizeMethodKey(pkg.method_key) === 'structural_method'
  );
  if (!structural) return { ok: true, offenders: [] };

  const offenders = (structural.items || []).filter((item) => {
    const normalized = normalizeDisputeMethod(item, item.assignment);
    return normalized.isCollection || normalized.method === 'debt_validation';
  });

  return {
    ok: offenders.length === 0,
    offenders,
  };
}

/**
 * Build method_groups payload for durable-generate from canonical packages.
 */
export function toMethodResolutionGroups(packages = []) {
  return (packages || []).map((pkg) => ({
    method_key: pkg.method_key,
    method_name: pkg.method_name || getCanonicalPackageLabel(pkg.method_key),
    method_badge: pkg.method_badge || pkg.method_key,
    count: pkg.count || (pkg.items || []).length,
    items: (pkg.items || []).map((item) => ({
      item_id: getStableDisputeItemId(item),
      assigned_method: normalizeMethodKey(item.method || item.resolved_method),
      dispute_family: item.disputeFamily || item.dispute_family,
      is_collection: Boolean(item.isCollection ?? item.is_collection),
      creditor_name:
        item.creditor_name
        || item.creditorName
        || item.account_name
        || null,
      original_creditor:
        item.original_creditor
        || item.originalCreditor
        || null,
    })),
  }));
}
